import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";

const benefits = [
  {
    icon: "fas fa-key",
    title: "Full ownership with no time restrictions",
  },
  {
    icon: "fas fa-chart-line",
    title: "Strong resale and rental potential",
  },
  {
    icon: "fas fa-map-marker-alt",
    title: "Access to premium residential communities",
  },
  {
    icon: "fas fa-home",
    title: "Ideal for families seeking space and privacy",
  },
];

const buyingSteps = [
  ["Understand Your Requirements", "We discuss your budget, preferred locations, and property goals."],
  ["Curated Property Selection", "You receive tailored options that match your criteria."],
  ["Property Viewings", "We arrange visits so you can explore each villa in detail."],
  ["Negotiation and Closing", "We guide you through offers, paperwork, and final ownership transfer."],
];

const faqs = [
  {
    icon: "fas fa-passport",
    question: "What are freehold villas in Abu Dhabi?",
    answer: "Freehold villas are properties that you fully own. You also own the land. You have full rights to lease, transfer ownership, or sell.",
  },
  {
    icon: "fas fa-globe",
    question: "Can foreigners buy freehold villas in Abu Dhabi?",
    answer: "Yes. Foreigners can buy freehold villas in designated investment zones approved by the government.",
  },
  {
    icon: "fas fa-chart-line",
    question: "Are freehold villas a good investment?",
    answer: "Yes. They offer long-term value, rental income potential, and full ownership benefits, making them a strong investment option.",
  },
  {
    icon: "fas fa-coins",
    question: "What costs are involved when buying a villa?",
    answer: "In addition to the price of the property, you have to pay the transfer fee, agency commission, and maintenance charges.",
  },
  {
    icon: "far fa-clock",
    question: "How long does the buying process take?",
    answer: "The process usually takes a few weeks, depending on documentation, financing, and negotiations.",
  },
  {
    icon: "fas fa-question-circle",
    question: "How do I choose the right villa?",
    answer: "Give us a call, tell us your needs, preferences, and budget, and we will filter all available properties to recommend the right villas to you. You can visit them and take your time to make your decision.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "Villas for Sale in Abu Dhabi", item: "https://nvrealestate.ae/villas-for-sale-abu-dhabi" },
  ],
};

function VillasForSaleAbuDhabi() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
     
      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/villas-for-sale-abu-dhabi/01.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner"> 
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi villa specialists</span>
              <h1 id="villa-page-title">Freehold Villas for Sale in Abu Dhabi – Own Your Space with Confidence</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Sale&city=&developer=&developer_name=&project=&project_name=&property_type=Villa&bedrooms=&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore properties
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section> 
        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img src="/images/seo/villas-for-sale-abu-dhabi/02.png"></img>
              <div className="villa-intro__note">
                <strong>Local expertise</strong>
                <span>For UAE residents and international buyers</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Freehold Villas for Sale in Abu Dhabi</h2>
              <p>
                Nova Vision Real Estate is the most applauded real estate company in Abu Dhabi for buying freehold villas for sale in Abu Dhabi. People trust us because they know we have a solution to all real estate requirements. When people come to us to buy freehold villas and explain their requirements, we filter all available options to find the best villas for sale in Abu Dhabi and recommend the best one suiting their needs. So many people have already taken our help to find the best freehold villas for sale in Abu Dhabi, and almost all of them appreciate us for our commitment to delivering them the best solutions. So, feel free to connect with us, and we will help you buy the right villas for your needs.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="villa-benefits-title">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Why freehold</span>
              <h2 id="villa-benefits-title">Why Choose Freehold Villas in Abu Dhabi?</h2>
              <p>Freehold villas offer long-term value and flexibility. Unlike other ownership types, freehold properties provide complete ownership rights, which gives you peace of mind. Here are some of the top reasons why people prefer freehold villas in Abu Dhabi. 
              When you explore Freehold Villas for Sale in Abu Dhabi, you are investing in both lifestyle and long-term security.</p>
      </div>
            <div className="villa-benefits__grid">
              {benefits.map((benefit, index) => (
                <article className="villa-benefit-card" key={benefit.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={benefit.icon} /></span>
                  <h3>{benefit.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-process villa-section" aria-labelledby="villa-process-title">
          <div className="container villa-process__layout">
            <div className="villa-process__aside">
              <span className="villa-kicker">Your buying journey</span>
              <h2 id="villa-process-title">A Buying Process That Works for You</h2>
              <p>Buying property should feel clear and manageable. Our process is to simplify every step.</p>
              <Link className="villa-text-link" to="/contact-us">Speak with an advisor <span aria-hidden="true">-&gt;</span></Link>
            </div>
            <div className="villa-process__steps-wrap">
              <ol className="villa-process__steps">
                {buyingSteps.map(([title, text], index) => (
                  <li key={title}>
                    <span className="villa-process__number">{String(index + 1).padStart(2, "0")}</span>
                    <div><h3>{title}</h3><p>{text}</p></div>
                  </li>
                ))}
              </ol>
              <p className="villa-process__closing">This structured approach ensures you make informed decisions with confidence.</p>
            </div>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Why Are We the Best Choice for Freehold Villas for Sale in Abu Dhabi</h2>
                <p>NV Real Estate is recognized as the most trustworthy name for freehold villas for sale in Abu Dhabi. People trust us because they know about our commitment to delivering them the best services. Here are some of the best reasons why we are the best choice for your needs to buy a freehold villa.</p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
                <p className="villa-advisor__closing">Call us now to get the best freehold villas for sale in Abu Dhabi.</p>
              </div>
              <ul>
                <li>We are a highly experienced team.</li>
                <li>Best service experience is our commitment.</li>
                <li>We have a connection with almost all trusted developers.</li>
                <li>We have access to even those properties that are not seen anywhere online.</li>
                <li>We help you at every step, from scratch to the end.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Buyer questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you begin your villa search with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`villa-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`villa-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-home" /><div><strong>Curated Villas</strong><span>Matched to Your Brief</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-map-marker-alt" /><div><strong>Local Knowledge</strong><span>Across Abu Dhabi</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Purchase Support</strong><span>Offer to Transfer</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email us at <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.</p>
          </div>
        </section>
      </main>

      <CTA />
    </>
  );
}

export default VillasForSaleAbuDhabi;
